import React, {useState, useEffect, useContext, useCallback} from 'react';
import {
    Box,
    CircularProgress,
    Chip,
    Typography,
    IconButton,
    Grid,
    Paper,
    Stack,
    Tabs,
    Tab,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Description as FileTextIcon,
    AccessTime as ClockIcon,
    LocalHospital as StethoscopeIcon,
    CheckCircle,
    ChevronLeft,
    Edit as EditIcon,
    ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import SelectedFaxContext from '../../fax/ReceivedFax/SelectedFaxContext';
import {MyButton, MyTypography, MyPdfViewer, MyJsonViewer} from '../../common/components';
import { useMutation } from '../../../common/apollo-graphql';
import ClinicDropdownHookFormSelect from '../../clinics/components/ClinicDropdownHookFormSelect';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {ASSOCIATE_FAX_TO_CLINIC_MUTATION} from '../../fax/gql/Mutation';
import {ReceivedFaxBlankDetails, useGetReceivedFaxDetails} from '../../fax/ReceivedFax/ReceivedFaxDetails';
import {PROCESSED_STATUS_RECEIVED_FAX} from '../../fax/constants/fax.consants';
import {isBoolean} from 'lodash';
import Swal from 'sweetalert2';

const initialValues = {
    clinicId: null,
};

const validationSchema = yup.object({
    clinicId: yup
        .number('Select the clinic')
        .required('Clinic is required')
        .typeError('Clinic is required'),
});

function useSaveFaxToClinicAssociation({selectedFax, onAssignClinic}) {
    const faxId = selectedFax
        ? selectedFax.id
        : null;

    const [isAssignClinic, setIsAssignClinic] = useState(false);

    const [saveClinicAssociation, {loading}] = useMutation(ASSOCIATE_FAX_TO_CLINIC_MUTATION, {
        fetchPolicy: 'no-cache',
    });

    const { handleSubmit, control, setValue, reset, formState: { errors } } = useForm({
        defaultValues: {...initialValues},
        resolver: yupResolver(validationSchema),
        shouldFocusError: true,
    });

    const handleOnEditClinic = useCallback((flag=null) => {
        setIsAssignClinic(prevState => isBoolean(flag) ? flag : !prevState);
    }, []);

    const _onSubmit = async (dataParams) => {
        const result = await saveClinicAssociation({
            variables: {clinicId: dataParams.clinicId, faxId},
        });

        onAssignClinic(result?.data?.save_fax_clinic_association);
        handleOnEditClinic(false);

        Swal.fire({
            icon: 'success',
            text: 'Successfully associated the fax to clinic.',
        });
    };

    useEffect(() => {
        if (selectedFax && selectedFax.clinic) {
            handleOnEditClinic(false);
            setValue('clinicId', selectedFax.clinic.id);
        } else {
            handleOnEditClinic(true);
        }

        return () => {
            handleOnEditClinic(false);
            reset();
        };
    }, [selectedFax, setValue, reset, handleOnEditClinic]);

    return {
        control,
        setValue,
        errors,
        loading,
        onSubmit: handleSubmit(_onSubmit),

        isAssignClinic,
        onEditClinic: handleOnEditClinic,
    };
}

function MyAccordion({id, children, title=null, subtitle=null}) {
    return (
        <Accordion sx={{width: '100%'}}>
            <AccordionSummary
                component={Paper}
                elevation={1}
                expandIcon={<ExpandMoreIcon />}
                id={id}
            >
                <Stack direction="column" spacing={0.5} alignItems="flex-start">
                {
                    title &&
                    <Typography variant="subtitle1">{title}</Typography>
                }
                {
                    subtitle &&
                    <Typography variant="caption">{subtitle}</Typography>
                }
                </Stack>
            </AccordionSummary>
            <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
    );
}

function FaxDetails({details}) {
    const metadata = details?.metadata ? JSON.parse(details.metadata) : null;
    const ocrData = details?.ocr_data ? JSON.parse(details.ocr_data) : null;

    return (
        <Stack direction="column"
               spacing={1}
               alignItems="flex-start"
               width="100%">
            <MyAccordion
                id="metadata"
                subtitle="Click to toggle"
                title="Metadata"
                sx={{width: '100%'}}
            >
                {
                    metadata
                    ? <MyJsonViewer data={metadata} />
                    : <MyTypography variant="caption">No metadata</MyTypography>
                }
            </MyAccordion>
            {
                ocrData &&
                <MyAccordion
                    id="ocr-data"
                    subtitle="Click to toggle"
                    title="OCR"
                    sx={{width: '100%'}}
                >
                    <MyJsonViewer data={ocrData} />
                </MyAccordion>
            }
        </Stack>
    );
}

export default function ReceivedFaxDetails({onCloseFaxDetails, onAssignClinic}) {
    const selectedFax = useContext(SelectedFaxContext);
    const [activeTab, setActiveTab] = useState(0);

    const assignedClinic = selectedFax && selectedFax.clinic
                            ? selectedFax.clinic.name
                            : null;

    const faxId = selectedFax
        ? selectedFax.id
        : null;

    const { loading, details, error } = useGetReceivedFaxDetails(faxId);

    const {
        control,
        setValue,
        errors,
        loading: loadingSave,
        onSubmit,

        isAssignClinic,
        onEditClinic
    } = useSaveFaxToClinicAssociation({selectedFax, onAssignClinic});

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (!selectedFax) {
        return (<ReceivedFaxBlankDetails />);
    }

    return (
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
            {/* Details Header */}
            <Box sx={{ borderBottom: 1, borderColor: "divider", p: 3 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <IconButton onClick={onCloseFaxDetails} sx={{ color: "text.secondary" }}>
                        <ChevronLeft />
                    </IconButton>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h4" fontWeight="bold">
                            Fax Details
                        </Typography>
                        <Stack direction="column"
                               spacing={1}
                               justifyContent="flex-start"
                               alignItems="flex-start">
                            <Typography color="text.secondary">
                                From {selectedFax.metadata.from_number} • Received {selectedFax.metadata.date_created}
                            </Typography>
                            {
                                assignedClinic
                                    ? <Chip
                                        color="info"
                                        label={assignedClinic}
                                        size="small"
                                        onDelete={onEditClinic}
                                        deleteIcon={<EditIcon />}
                                    />
                                    : <Chip label="Unidentified clinic" size="small" />
                            }
                        </Stack>
                    </Box>
                    <Stack direction="row" alignItems="flex-start">
                        <Chip
                            icon={selectedFax.status === PROCESSED_STATUS_RECEIVED_FAX ? <CheckCircle /> : <ClockIcon />}
                            label={selectedFax.status}
                            color={selectedFax.status === PROCESSED_STATUS_RECEIVED_FAX ? "success" : "default"}
                        />
                    </Stack>

                </Box>
            </Box>

            <Box sx={{ flex: 1, p: 1, overflowY: "auto" }}>
                <Paper sx={{mb: 1}}>
                    <Tabs value={activeTab} onChange={handleTabChange} variant="fullWidth">
                        <Tab icon={<StethoscopeIcon />} label="Clinic" iconPosition="start" />
                        <Tab icon={<FileTextIcon />} label="Fax Details" iconPosition="start" />
                    </Tabs>
                </Paper>

                {
                    activeTab === 1 &&
                    <FaxDetails details={details} />
                }

                {
                    activeTab === 0 &&
                    <form onSubmit={onSubmit}
                          style={{
                              width: '100%',
                          }}>
                        <Grid container
                              direction="row">
                            <Grid item xs={12} md={isAssignClinic ? 6 : 12}>
                                {/* PDF Viewer Placeholder */}
                                <Paper
                                    sx={{
                                        textAlign: 'center',
                                        overflowY: 'hidden',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {/* Wrap PDF Preview in a Scrollable Container */}
                                    {
                                        loading
                                            ? <Box>
                                                <FileTextIcon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}/>
                                                <Typography variant="h6" gutterBottom>
                                                    PDF Viewer
                                                </Typography>
                                                <Typography color="text.secondary">Please wait loading pdf now...</Typography>
                                                <CircularProgress/>
                                            </Box>
                                            : (
                                                details && details.pdf_blob_url
                                                    ? <Box
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                        }}
                                                    >
                                                        <MyPdfViewer fileUrl={details.pdf_blob_url}/>
                                                    </Box>
                                                    : <MyTypography>No pdf loaded</MyTypography>
                                            )
                                    }
                                </Paper>
                            </Grid>
                            {
                                isAssignClinic &&
                                <Grid item xs={12} md={6}>
                                    <Stack direction="column"
                                           spacing={1} sx={{py: 4, px: 2}}>
                                        <MyTypography>Select a Clinic to assign below</MyTypography>
                                        <ClinicDropdownHookFormSelect
                                            schema="clinicId"
                                            control={control}
                                            setValue={setValue}
                                            errors={errors}
                                            variant="outlined"
                                            label={null}
                                            fullWidth
                                        />
                                        <Stack direction="row"
                                               justifyContent="flex-end"
                                               alignItems="flex-start"
                                               spacing={1}
                                               sx={{pt: 1}}>
                                            <MyButton variant="outlined"
                                                      color="primary"
                                                      fullWidth={false}
                                                      disabled={loadingSave}
                                                      onClick={() => onEditClinic(false)}
                                                      sx={{minWidth: 180}}>Cancel</MyButton>
                                            <MyButton type="submit"
                                                      variant="contained"
                                                      color="primary"
                                                      fullWidth={false}
                                                      loading={loadingSave}
                                                      sx={{minWidth: 180}}>Save</MyButton>
                                        </Stack>
                                    </Stack>
                                </Grid>
                            }
                        </Grid>
                    </form>
                }
            </Box>
        </Box>
    );
}